import { Component, type ErrorInfo, type ReactNode } from 'react'

interface MapBoundaryProps {
  children: ReactNode
  onError: (error: Error) => void
}

interface MapBoundaryState {
  failed: boolean
}

/**
 * The one error boundary in the app, and it exists for one specific failure:
 * the dynamic `import()` of `atlas_map` rejecting.
 *
 * `Suspense` covers a lazy import while it is *pending* and nothing else — a
 * rejected import is rethrown during render. With no boundary above it React
 * unmounts the entire root, so a 304 KB chunk that 404s after a deploy, or that
 * an extension blocks, takes the header, the search box, the layer toggles and
 * the atlas list down with it and leaves a blank page. That is a strictly worse
 * outcome than the WebGL2 fallback this app already has, and it is the more
 * likely of the two: a stale chunk hash is a routine consequence of deploying
 * while someone has the page open.
 *
 * `MapUnsupported` does not cover it. That notice renders *inside* `AtlasMap`,
 * which by definition never mounted here.
 *
 * A class, because `getDerivedStateFromError` has no hook equivalent — React
 * still offers no functional error boundary. `onError` is called from
 * `componentDidCatch` rather than from the state update, so the parent's
 * `setState` happens in the commit phase instead of during another component's
 * render.
 */
export default class MapBoundary extends Component<MapBoundaryProps, MapBoundaryState> {
  state: MapBoundaryState = { failed: false }

  static getDerivedStateFromError(): MapBoundaryState {
    return { failed: true }
  }

  componentDidCatch(error: Error, _info: ErrorInfo) {
    this.props.onError(error)
  }

  render() {
    // Renders nothing on failure: the notice and the readable atlas list both
    // live in the layout, which the `onError` callback has just switched into
    // its map-failed state.
    return this.state.failed ? null : this.props.children
  }
}
